import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';

import { isSigningIn, useSignIn } from '../../hooks/useSignIn';
import { mfaCodeSchema, type MfaCodeValues, normalizeMfaCode } from '../../lib/authFormSchema';
import {
  type LoginCredentials,
  type MfaChallenge,
  type MfaMethod,
  rudderstackLogin,
} from '../../lib/rudderstack-auth-client';
import { StepHeading } from './StepHeading';
import { TextButton } from './TextLink';

const METHOD_COPY: Record<MfaMethod, { description: string; label: string; placeholder: string }> = {
  TOTP: {
    description: 'Enter the 6-digit code from your authenticator app.',
    label: 'Authentication code',
    placeholder: '123456',
  },
  SMS: {
    description: 'Enter the 6-digit code we sent to your phone.',
    label: 'Text message code',
    placeholder: '123456',
  },
  BACKUP_CODE: {
    description: 'Enter one of the backup codes you saved when you set up two-factor authentication.',
    label: 'Backup code',
    placeholder: 'XXXX-XXXX-XXXX',
  },
};

const TIMED_OUT_NOTICE: Record<MfaMethod, string> = {
  TOTP: 'Verification timed out. Enter a new code.',
  SMS: 'Verification timed out. We sent a new code to your phone.',
  BACKUP_CODE: 'Verification timed out. Enter the backup code again.',
};

/** Keeps the digits of a typed or pasted one-time code, so `123 456` fits the 6-digit field. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/** The second `/login` phase: a code from the authenticator app, a text message or a backup code. */
export function MfaCodeStep({
  apiBaseUrl,
  credentials,
  challenge: initialChallenge,
  returnTo,
  onSetupRequired,
  onBack,
}: {
  apiBaseUrl: string;
  credentials: LoginCredentials;
  challenge: MfaChallenge;
  returnTo: string | null;
  onSetupRequired: () => void;
  onBack: () => void;
}): React.ReactElement {
  const [challenge, setChallenge] = useState(initialChallenge);
  const [notice, setNotice] = useState<string | null>(null);
  const form = useForm<MfaCodeValues>({
    resolver: zodResolver(mfaCodeSchema),
    mode: 'onSubmit',
    defaultValues: { method: initialChallenge.method, code: '' },
  });
  const method = useWatch({ control: form.control, name: 'method' });
  const verify = useSignIn(apiBaseUrl, returnTo);
  const sendSms = useMutation({
    // An expired session token comes back as a fresh challenge, and then config-backend sends a text only when
    // SMS is the user's only method. Only an SMS challenge in the reply means that a text went out.
    mutationFn: async ({ sessionToken }: { sessionToken: string; notice: string }) => {
      const first = await rudderstackLogin(apiBaseUrl, { ...credentials, sessionToken, sendSmsOtp: true });
      if (first.kind !== 'mfa_challenge' || first.challenge.method === 'SMS') return first;
      const second = await rudderstackLogin(apiBaseUrl, {
        ...credentials,
        sessionToken: first.challenge.sessionToken,
        sendSmsOtp: true,
      });
      if (second.kind === 'mfa_challenge' && second.challenge.method !== 'SMS') {
        throw new Error('We could not send a text message. Try again.');
      }
      return second;
    },
    gcTime: 0,
    onSuccess: (result, { notice: sentNotice }) => {
      if (result.kind !== 'mfa_challenge') return;
      setChallenge(result.challenge);
      form.reset({ method: 'SMS', code: '' });
      setNotice(sentNotice);
    },
  });
  // The automatic text after an expired SMS code counts too, so no second request starts meanwhile.
  const busy = isSigningIn(verify) || sendSms.isPending;
  const copy = METHOD_COPY[method];
  const has = (candidate: MfaMethod): boolean => challenge.availableMethods.includes(candidate);

  const switchTo = (next: MfaMethod): void => {
    form.reset({ method: next, code: '' });
    setNotice(null);
    verify.reset();
    sendSms.reset();
  };

  const requestSms = (): void => {
    setNotice(null);
    verify.reset();
    sendSms.mutate({ sessionToken: challenge.sessionToken, notice: 'We sent a new code to your phone.' });
  };

  const onValid = (values: MfaCodeValues): void => {
    setNotice(null);
    sendSms.reset();
    verify.mutate(
      {
        ...credentials,
        sessionToken: challenge.sessionToken,
        method: values.method,
        code: normalizeMfaCode(values.method, values.code),
      },
      {
        onSuccess: (outcome) => {
          if (outcome.kind === 'mfa_setup') onSetupRequired();
          if (outcome.kind !== 'mfa_challenge') return;
          // An expired session token comes back as a fresh challenge, with a text only when SMS is the only method.
          setChallenge(outcome.challenge);
          form.reset({ method: values.method, code: '' });
          if (values.method === 'SMS' && outcome.challenge.method !== 'SMS') {
            sendSms.mutate({
              sessionToken: outcome.challenge.sessionToken,
              notice: 'Verification timed out. We sent a new code to your phone.',
            });
          } else {
            setNotice(TIMED_OUT_NOTICE[values.method]);
          }
        },
      },
    );
  };

  const error = verify.error ?? sendSms.error;

  return (
    <div className="flex flex-col">
      <StepHeading title="Two-factor authentication" description={copy.description} />
      <div className="mt-6 flex flex-col gap-4 text-left">
        {error ? (
          <Callout variant="error" role="alert">
            {error.message}
          </Callout>
        ) : notice ? (
          <Callout variant="info" role="status">
            {notice}
          </Callout>
        ) : null}
        <Form {...form}>
          <form
            noValidate
            aria-label="Two-factor authentication"
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(onValid)}
          >
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{copy.label}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      // The browser's maxLength would cut a pasted `123 456` before the spaces go.
                      onChange={(event) =>
                        field.onChange(method === 'BACKUP_CODE' ? event.target.value : digitsOnly(event.target.value))
                      }
                      size="lg"
                      autoFocus
                      autoComplete="one-time-code"
                      inputMode={method === 'BACKUP_CODE' ? 'text' : 'numeric'}
                      maxLength={method === 'BACKUP_CODE' ? 14 : undefined}
                      placeholder={copy.placeholder}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
              {isSigningIn(verify) ? 'Verifying…' : 'Verify'}
            </Button>
          </form>
        </Form>
      </div>
      <div className="mt-4 flex flex-col items-center gap-2">
        {method !== 'TOTP' && has('TOTP') && (
          <TextButton tone="primary" disabled={busy} onClick={() => switchTo('TOTP')}>
            Use your authenticator app
          </TextButton>
        )}
        {has('SMS') && (
          <TextButton tone="primary" disabled={busy} loading={sendSms.isPending} onClick={requestSms}>
            {method === 'SMS' ? 'Send a new code' : 'Send a code by text message'}
          </TextButton>
        )}
        {method !== 'BACKUP_CODE' && has('BACKUP_CODE') && (
          <TextButton tone="primary" disabled={busy} onClick={() => switchTo('BACKUP_CODE')}>
            Use a backup code
          </TextButton>
        )}
        <TextButton disabled={busy} onClick={onBack}>
          Back to sign in
        </TextButton>
      </div>
    </div>
  );
}
